<template>
    <footer class="site-footer">
  
      <!-- Top section -->
      <div class="footer-top">
        <div class="footer-inner">
  
          <!-- Brand + Newsletter -->
          <div class="footer-brand">
            <router-link to="/home" class="footer-logo-link">
              <img
                v-if="details.logo"
                :src="url.hostName + 'images/logo/' + details.logo"
                alt="Logo"
                class="footer-logo"
              />
              <img v-else src="/static/img/logo.png" alt="Logo" class="footer-logo" />
            </router-link>
  
            <p class="footer-tagline">
              Modular Accounting, Payroll, Inventory, Invoicing and Billing System
              that captures all process automation.
            </p>
  
            <!-- Newsletter -->
            <div class="newsletter-wrap">
              <p class="newsletter-label">Stay in the loop</p>
              <form class="newsletter-form" @submit.prevent="sendNewsletter">
                <input
                  type="email"
                  v-model="newsletter_email"
                  class="newsletter-input"
                  placeholder="your@email.com"
                  required
                />
                <button type="submit" class="newsletter-btn" :disabled="loading">
                  <font-awesome-icon v-if="loading" icon="spinner" spin />
                  <span v-else>Subscribe</span>
                </button>
              </form>
            </div>
          </div>
  
          <!-- Links -->
          <div class="footer-links-grid">
  
            <div class="footer-col">
              <h6 class="footer-col-title">Company</h6>
              <ul class="footer-col-list">
                <li><router-link to="/home">Home</router-link></li>
                <li><router-link to="/about">About Us</router-link></li>
                <li><router-link to="/contact">Contact</router-link></li>
                <li><router-link to="/home">Documentation</router-link></li>
              </ul>
            </div>
  
            <div class="footer-col">
              <h6 class="footer-col-title">Resources</h6>
              <ul class="footer-col-list">
                <li><router-link to="/blog">Blog</router-link></li>
                <li><router-link to="/faq">FAQs</router-link></li>
                <li><router-link to="/pricing">Pricing</router-link></li>
                <li><router-link to="/auth/register">Get Started</router-link></li>
              </ul>
            </div>
  
            <div class="footer-col">
              <h6 class="footer-col-title">Legal</h6>
              <ul class="footer-col-list">
                <li><router-link to="/privacy-policy">Privacy Policy</router-link></li>
                <li><router-link to="/cookie-policy">Cookie Policy</router-link></li>
                <li><router-link to="/terms-and-conditions">Terms &amp; Conditions</router-link></li>
              </ul>
            </div>
  
            <div class="footer-col">
              <h6 class="footer-col-title">Contact</h6>
              <address class="footer-address">
                <p v-if="details.address">{{ details.address }}</p>
                <a v-if="details.email" :href="'mailto:' + details.email">{{ details.email }}</a>
                <a v-if="details.tel"   :href="'tel:' + details.tel">{{ details.tel }}</a>
                <a v-if="details.tel2"  :href="'tel:' + details.tel2">{{ details.tel2 }}</a>
              </address>
  
              <!-- Social icons -->
              <div class="social-row">
                <a
                  v-if="details.facebook"
                  :href="details.facebook"
                  target="_blank"
                  rel="noopener"
                  class="social-btn"
                  aria-label="Facebook"
                >
                  <i class="fab fa-facebook-f"></i>
                </a>
                <a
                  v-if="details.twitter"
                  :href="details.twitter"
                  target="_blank"
                  rel="noopener"
                  class="social-btn"
                  aria-label="Twitter"
                >
                  <i class="fab fa-twitter"></i>
                </a>
                <a
                  v-if="details.linkedin"
                  :href="details.linkedin"
                  target="_blank"
                  rel="noopener"
                  class="social-btn"
                  aria-label="LinkedIn"
                >
                  <i class="fab fa-linkedin-in"></i>
                </a>
                <a
                  v-if="details.instagram"
                  :href="details.instagram"
                  target="_blank"
                  rel="noopener"
                  class="social-btn"
                  aria-label="Instagram"
                >
                  <i class="fab fa-instagram"></i>
                </a>
              </div>
            </div>
  
          </div>
        </div>
      </div>
  
      <!-- Bottom bar -->
      <div class="footer-bottom">
        <div class="footer-inner footer-bottom-inner">
          <p class="footer-copy">
            &copy; {{ currentYear }} {{ details.shortname || 'Mapiibs' }}. All rights reserved.
          </p>
          <nav class="footer-bottom-nav" aria-label="Legal navigation">
            <router-link to="/privacy-policy">Privacy Policy</router-link>
            <router-link to="/cookie-policy">Cookie Policy</router-link>
            <router-link to="/terms-and-conditions">Terms &amp; Conditions</router-link>
          </nav>
        </div>
      </div>
  
    </footer>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import hostName from '../main';
  
  export default {
    name: 'PublicFooter',
  
    data() {
      return {
        details:          {},
        newsletter_email: '',
        loading:          false,
        toast:            useToast(),
        url:              hostName,
      };
    },
  
    computed: {
      currentYear() {
        return new Date().getFullYear();
      },
    },
  
    methods: {
      pageData() {
        axios.get('/admin/fetchProjectSetup')
          .then(res => { this.details = res.data.data; })
          .catch(() => {});
      },
  
      async sendNewsletter() {
        this.loading = true;
        try {
          const { data } = await axios.post('/send-newsletter', { email: this.newsletter_email });
          this.toast.success(data.message);
          this.newsletter_email = '';
        } catch (err) {
          const status = err.response?.status;
          if (status === 422 || status === 400) {
            this.toast.error(err.response.data.message);
          } else {
            this.toast.error('An error occurred. Please try again.');
          }
        } finally {
          this.loading = false;
        }
      },
    },
  
    mounted() {
      this.pageData();
    },
  };
  </script>
  
  <style scoped>
  /* ── Footer shell ────────────────────────────────────────────────────── */
  .site-footer {
    background: #051B35;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13.5px;
  }
  
  /* ── Inner width ─────────────────────────────────────────────────────── */
  .footer-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
  }
  
  /* ── Top section ─────────────────────────────────────────────────────── */
  .footer-top {
    padding: 64px 0 48px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }
  
  .footer-top .footer-inner {
    display: grid;
    grid-template-columns: 1.6fr 2.4fr;
    gap: 64px;
    align-items: start;
  }
  
  /* ── Brand column ────────────────────────────────────────────────────── */
  .footer-brand { display: flex; flex-direction: column; gap: 20px; }
  
  .footer-logo-link { display: inline-block; }
  .footer-logo      { max-height: 52px; width: auto; object-fit: contain; }
  
  .footer-tagline {
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.65;
    font-size: 13px;
    margin: 0;
    max-width: 300px;
  }
  
  /* ── Newsletter ──────────────────────────────────────────────────────── */
  .newsletter-wrap  { display: flex; flex-direction: column; gap: 10px; }
  .newsletter-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .07em; color: rgba(255,255,255,.4); margin: 0; }
  
  .newsletter-form {
    display: flex;
    gap: 0;
    border-radius: 9px;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,.12);
    background: rgba(255,255,255,.05);
    transition: border-color .2s;
  }
  .newsletter-form:focus-within { border-color: rgba(255,255,255,.28); }
  
  .newsletter-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    padding: 10px 14px;
    color: #fff;
    font-size: 13px;
  }
  .newsletter-input::placeholder { color: rgba(255,255,255,.3); }
  
  .newsletter-btn {
    background: #004AAD;
    border: none;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    padding: 10px 18px;
    cursor: pointer;
    white-space: nowrap;
    transition: background .15s;
    flex-shrink: 0;
  }
  .newsletter-btn:hover:not(:disabled) { background: #0059d6; }
  .newsletter-btn:disabled { opacity: .6; cursor: not-allowed; }
  
  /* ── Links grid ──────────────────────────────────────────────────────── */
  .footer-links-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
  }
  
  /* ── Link columns ────────────────────────────────────────────────────── */
  .footer-col-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: rgba(255,255,255,.35);
    margin: 0 0 16px;
  }
  
  .footer-col-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .footer-col-list a,
  .footer-col-list router-link {
    color: rgba(255, 255, 255, 0.62);
    text-decoration: none;
    font-size: 13.5px;
    transition: color .15s;
  }
  .footer-col-list a:hover { color: #fff; }
  
  /* ── Address ─────────────────────────────────────────────────────────── */
  .footer-address {
    display: flex;
    flex-direction: column;
    gap: 7px;
    font-style: normal;
    margin-bottom: 16px;
  }
  .footer-address p,
  .footer-address a {
    color: rgba(255,255,255,.62);
    font-size: 13px;
    text-decoration: none;
    margin: 0;
    line-height: 1.4;
    transition: color .15s;
  }
  .footer-address a:hover { color: #fff; }
  
  /* ── Social icons ────────────────────────────────────────────────────── */
  .social-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .social-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,.12);
    background: rgba(255,255,255,.05);
    color: rgba(255,255,255,.65);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    text-decoration: none;
    transition: background .15s, color .15s, border-color .15s;
  }
  .social-btn:hover {
    background: rgba(255,255,255,.12);
    color: #fff;
    border-color: rgba(255,255,255,.25);
  }
  
  /* ── Bottom bar ──────────────────────────────────────────────────────── */
  .footer-bottom { padding: 20px 0; }
  
  .footer-bottom-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .footer-copy {
    margin: 0;
    font-size: 12.5px;
    color: rgba(255,255,255,.35);
  }
  
  .footer-bottom-nav {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
  .footer-bottom-nav a {
    font-size: 12.5px;
    color: rgba(255,255,255,.35);
    text-decoration: none;
    transition: color .15s;
  }
  .footer-bottom-nav a:hover { color: rgba(255,255,255,.7); }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width: 1024px) {
    .footer-top .footer-inner { grid-template-columns: 1fr; gap: 40px; }
    .footer-tagline { max-width: 100%; }
    .footer-links-grid { grid-template-columns: repeat(2, 1fr); gap: 28px; }
  }
  
  @media (max-width: 480px) {
    .footer-links-grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .footer-bottom-inner { flex-direction: column; align-items: flex-start; }
    .footer-top { padding: 40px 0 32px; }
  }
  </style>